import React, { useMemo } from 'react';
import { JobType } from '../graphql/types';
import { WorkspaceRoute } from './WorkspaceRoute';
import { LoanRail } from './LoanRail';
import { DocumentRail } from './DocumentRail';
import { getLockedModes } from './rowMode';
import { getDraftTitle } from './draftModel';
import { getDraftStatusLabel } from './workflowStatus';
import IntakeTR from './intake/IntakeTR';
import DraftIntakeDD from './intake/DraftIntakeDD';
import DraftIntakeBA from './intake/DraftIntakeBA';

/**
 * DraftWorkspace: the Workspace a "New job / New loan / New title search"
 * button opens, instead of the old create modal.
 *
 * It is the normal Workspace shell in Intake Mode for an item that does not
 * exist yet, with every other Mode locked and a reason in the segment's
 * `title` until the item is created (Requirements 2.3, 3.5).
 *
 * - TR: `IntakeTR` collects the order and documents and creates the search on
 *   submit, then opens Review.
 * - DD: `DraftIntakeDD` is the empty loan-list card ("Drop a loan tape, or add
 *   loans by hand") with Stage 2 locked. The Rail shows no loans and the
 *   Status_Pill reads "Draft · no loans yet".
 * - BA: `DraftIntakeBA` is the empty loan form, drop zone and checklist. The
 *   Rail lists the standard documents as missing and the Status_Pill reads
 *   "Draft".
 *
 * DD and BA need a real job id for loans and uploads, so there is no create
 * form: the job is created on the first real action with a default name, and
 * the URL is replaced with `/{feature}/{jobId}?mode=intake` (see
 * `useDraftJob`).
 *
 * Light theme only.
 */
export interface DraftWorkspaceProps {
  feature: JobType;
}

export const DraftWorkspace: React.FC<DraftWorkspaceProps> = ({ feature }) => {
  const locked = useMemo(() => getLockedModes(feature, null, { isDraft: true }), [feature]);

  let rail: React.ReactNode;
  let body: React.ReactNode;
  if (feature === JobType.DD) {
    rail = <LoanRail loans={[]} />;
    body = <DraftIntakeDD />;
  } else if (feature === JobType.BA) {
    rail = <DocumentRail documents={[]} />;
    body = <DraftIntakeBA />;
  } else {
    body = <IntakeTR />;
  }

  return (
    <WorkspaceRoute
      feature={feature}
      title={getDraftTitle(feature)}
      statusLabel={getDraftStatusLabel(feature)}
      statusTone="neutral"
      defaultMode="intake"
      locked={locked}
      rail={rail}
      railLabel={feature === JobType.BA ? 'Documents' : 'Loans'}
    >
      <div className="p-6">{body}</div>
    </WorkspaceRoute>
  );
};

export default DraftWorkspace;
